export type ReferenceFileLike = {
    name?: unknown;
    type?: unknown;
    size?: unknown;
};

const GENERIC_BINARY_CONTENT_TYPES = new Set(['', 'application/octet-stream']);

function normaliseContentType(value: unknown): string {
    return (String(value || '').split(';', 1)[0] || '').trim().toLowerCase();
}

function filenameExtension(value: unknown): string {
    const name = String(value || '').trim().toLowerCase();
    const dot = name.lastIndexOf('.');
    return dot > 0 && dot < name.length - 1 ? name.slice(dot + 1) : '';
}

// This is a browser-memory routing threshold, not an ingestion or upload cap.
// Above it, pdf.js receives a Blob-backed range transport rather than one
// contiguous ArrayBuffer. Semantic extraction continues page-by-page.
export const PDF_BROWSER_RANGE_ROUTE_THRESHOLD_BYTES = 64 * 1024 * 1024;
export const PDF_BROWSER_MEMORY_ROUTE_THRESHOLD_BYTES = PDF_BROWSER_RANGE_ROUTE_THRESHOLD_BYTES;

/**
 * Identify a PDF from either its filename or browser-supplied MIME type.
 *
 * Browser file pickers are not consistent about populating `File.type`, while
 * drag/drop clients can preserve the MIME type even when a generated filename
 * has no useful extension. Supporting both signals keeps every Stage 1 import
 * surface on the same PDF size policy.
 */
export function isPdfReferenceFile(file: ReferenceFileLike | null | undefined): boolean {
    if (hasContradictoryPdfSignals(file)) return false;
    return filenameExtension(file?.name) === 'pdf'
        || normaliseContentType(file?.type) === 'application/pdf';
}

/**
 * Reject only a known filename/MIME disagreement. Missing or generic browser
 * MIME metadata remains eligible for extension fallback, but a declared image,
 * video, or Office MIME cannot borrow the unbounded PDF lane (and vice versa).
 */
export function hasContradictoryPdfSignals(
    file: ReferenceFileLike | null | undefined,
): boolean {
    const extension = filenameExtension(file?.name);
    const contentType = normaliseContentType(file?.type);
    if (!extension || GENERIC_BINARY_CONTENT_TYPES.has(contentType)) return false;
    const extensionSaysPdf = extension === 'pdf';
    const mimeSaysPdf = contentType === 'application/pdf';
    return extensionSaysPdf !== mimeSaysPdf && (extensionSaysPdf || mimeSaysPdf);
}

export function shouldUsePdfRangeTransport(
    file: ReferenceFileLike | null | undefined,
): boolean {
    const bytes = Number(file?.size);
    return isPdfReferenceFile(file)
        && Number.isFinite(bytes)
        && bytes > PDF_BROWSER_RANGE_ROUTE_THRESHOLD_BYTES;
}
